// Pure decision logic for federated Google link/recovery failures.
//
// Extracted from header.js so it can be unit-tested: header.js touches the DOM
// at module scope and this repo has no DOM harness, so anything left in there
// can only be "tested" by grepping its source.

/**
 * How to treat a failed POST /oauth/v1/federated/link/confirm.
 *
 *  "retry"        – transient; keep the continuation and let the user retry.
 *  "rate-limited" – 429 from the login limiter. The request never reached
 *                   consumeLinkTransaction, so the continuation is still valid;
 *                   waiting is the only useful advice.
 *  "expired"      – the transaction is spent, mismatched or gone (409). Nothing
 *                   a retry can do; drop the continuation so it stops
 *                   re-firing the modal on every /login load.
 *  "session-lost" – 401. The session went away, so the transaction can no
 *                   longer match it. Also terminal, but it needs a different
 *                   message: closing the dialog in silence leaves the user
 *                   with no idea why linking stopped.
 */
const classifyGoogleLinkError = (status) => {
  if (status === 429) return "rate-limited";
  if (status === 401) return "session-lost";
  if (status >= 400 && status < 500) return "expired";
  return "retry";
};

const GOOGLE_LINK_MESSAGES = {
  "rate-limited":
    "Too many attempts. Please wait a moment, then try linking Google again.",
  "session-lost":
    "Your session expired before Google could be linked. Sign in with Google again to finish.",
  expired:
    "This Google linking request expired or was already used. Sign in with Google again to restart.",
  retry: "We verified your account but couldn't link Google. Please try again.",
};

// Terminal outcomes must drop the stored continuation. A rate limit must not:
// its continuation is untouched and still usable.
const isGoogleLinkTerminal = (outcome) =>
  outcome === "expired" || outcome === "session-lost";

/**
 * Message for a failed signup-recovery attempt (POST resumeSignup).
 * 429 here is the per-account recovery limiter — telling the user to "try
 * again" would just burn their next attempt.
 */
const signupRecoveryMessage = (status, errorCode, retryAfterSeconds) => {
  // The server's own backoff is more specific than the rate limiter, so it is
  // checked first: it can tell the user how long to wait rather than guessing.
  if (errorCode === "SIGNUP_RECOVERY_BACKOFF") {
    const waitMinutes = Math.max(1, Math.ceil(Number(retryAfterSeconds || 0) / 60));
    return `Account setup needs a short pause. Please retry in about ${waitMinutes} minute${
      waitMinutes === 1 ? "" : "s"
    }.`;
  }
  if (status === 429) {
    return "Too many setup attempts. Please wait a few minutes, then retry.";
  }
  if (errorCode === "SIGNUP_RECOVERY_IN_PROGRESS") {
    return "Account setup is already running. Wait a moment, then retry.";
  }
  return "Setup still could not finish. Please try again.";
};

module.exports = {
  classifyGoogleLinkError,
  GOOGLE_LINK_MESSAGES,
  isGoogleLinkTerminal,
  signupRecoveryMessage,
};
